import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatINR, formatDate } from "@/lib/format";

// PDF-safe currency formatter — jsPDF's default fonts don't include the ₹
// glyph and render it as a "+" or box. Use "Rs." instead for PDFs.
function formatRsPdf(amount) {
  return formatINR(amount).replace(/₹\s?/, "Rs. ");
}

/**
 * Resolve which firm the bill belongs to, given the (multi-firm) businessInfo payload.
 * Falls back to the top-level businessInfo (single-firm shape) if no firms list is present.
 */
export function resolveFirm(bill, businessInfo) {
  if (!businessInfo) return null;
  const firms = businessInfo.firms;
  const targetKey = bill?.firm || businessInfo.default_firm;
  if (Array.isArray(firms) && firms.length > 0) {
    const match = firms.find((f) => f.key === targetKey);
    if (match) return match;
    return firms[0];
  }
  // Legacy single-firm shape
  return {
    key: "default",
    name: businessInfo.business_name || "Billing",
    tagline: businessInfo.business_tagline || "",
    bank: businessInfo.bank || {},
    payment_note: businessInfo.payment_note || "",
  };
}

/**
 * Generate the invoice PDF.
 * @param {object} bill
 * @param {object|null} businessInfo
 * @param {{returnBlob?: boolean}} [opts]
 * @returns {Blob|undefined} If opts.returnBlob is true, returns the PDF Blob and does NOT trigger a download.
 */
export function generateInvoicePDF(bill, businessInfo = null, opts = {}) {
  const firm = resolveFirm(bill, businessInfo);
  const businessName = firm?.name || "Billing";
  const tagline = firm?.tagline || "";
  const bank = firm?.bank || null;
  const paymentNote = firm?.payment_note || "";

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(businessName, 40, 60);

  if (tagline) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(tagline, 40, 78);
  }

  // Invoice title / meta
  doc.setTextColor(15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.text("INVOICE", pageWidth - 40, 60, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(`Invoice #: ${bill.invoice_no}`, pageWidth - 40, 78, { align: "right" });
  doc.text(`Date: ${formatDate(bill.created_at)}`, pageWidth - 40, 92, { align: "right" });
  if (bill.due_date) {
    doc.text(`Due: ${formatDate(bill.due_date)}`, pageWidth - 40, 106, { align: "right" });
  }
  if (bill.created_by_name) {
    doc.text(
      `Prepared by: ${bill.created_by_name}`,
      pageWidth - 40,
      bill.due_date ? 120 : 106,
      { align: "right" }
    );
  }

  doc.setDrawColor(220);
  doc.line(40, 120, pageWidth - 40, 120);

  // Bill To
  doc.setTextColor(120);
  doc.setFontSize(9);
  doc.text("BILL TO", 40, 145);

  doc.setTextColor(15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(bill.client_name || "", 40, 162);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(bill.client_email || "", 40, 178);

  // Status badge
  const statusText = (bill.status || "unpaid").toUpperCase();
  const isPaid = bill.status === "paid";
  doc.setFillColor(isPaid ? 209 : 254, isPaid ? 250 : 226, isPaid ? 229 : 226);
  doc.setDrawColor(isPaid ? 34 : 220, isPaid ? 197 : 38, isPaid ? 94 : 38);
  doc.roundedRect(pageWidth - 130, 135, 90, 26, 3, 3, "FD");
  doc.setTextColor(isPaid ? 22 : 159, isPaid ? 101 : 18, isPaid ? 52 : 57);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(statusText, pageWidth - 85, 152, { align: "center" });

  // Services table
  const rows = (bill.services || []).map((s, i) => [
    String(i + 1),
    s.description,
    formatRsPdf(s.fee),
  ]);

  autoTable(doc, {
    startY: 210,
    head: [["#", "Service Description", "Amount"]],
    body: rows,
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 10,
      cellPadding: 8,
      lineColor: [220, 220, 220],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: 40, halign: "center" },
      2: { halign: "right", cellWidth: 120 },
    },
  });

  let cursorY = doc.lastAutoTable.finalY || 260;

  // Total
  doc.setDrawColor(15);
  doc.setLineWidth(1);
  doc.line(pageWidth - 240, cursorY + 20, pageWidth - 40, cursorY + 20);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(15);
  doc.text("TOTAL", pageWidth - 240, cursorY + 42);
  doc.setFontSize(14);
  doc.text(formatRsPdf(bill.total), pageWidth - 40, cursorY + 42, { align: "right" });

  cursorY += 60;

  // Notes
  if (bill.notes) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text("Notes:", 40, cursorY + 20);
    const noteLines = doc.splitTextToSize(bill.notes, pageWidth - 80);
    doc.text(noteLines, 40, cursorY + 34);
    cursorY += 30 + noteLines.length * 12;
  }

  // Bank details (only show if unpaid & we have them)
  if (bank && bank.account_number && !isPaid) {
    cursorY += 20;
    doc.setDrawColor(220);
    doc.setLineWidth(0.5);
    doc.line(40, cursorY, pageWidth - 40, cursorY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15);
    doc.text("HOW TO PAY", 40, cursorY + 20);

    const bankRows = [];
    if (bank.account_name) bankRows.push(["Account Name", bank.account_name]);
    if (bank.bank_name) bankRows.push(["Bank", bank.bank_name]);
    bankRows.push(["Account No.", bank.account_number]);
    if (bank.ifsc) bankRows.push(["IFSC", bank.ifsc]);
    if (bank.branch) bankRows.push(["Branch", bank.branch]);
    if (bank.upi) bankRows.push(["UPI", bank.upi]);

    autoTable(doc, {
      startY: cursorY + 30,
      body: bankRows,
      theme: "plain",
      styles: { font: "helvetica", fontSize: 10, cellPadding: 6 },
      columnStyles: {
        0: { cellWidth: 100, textColor: [100, 116, 139], fontStyle: "normal" },
        1: { textColor: [15, 23, 42], fontStyle: "bold" },
      },
    });

    if (paymentNote) {
      const finalY = doc.lastAutoTable.finalY || cursorY + 30;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(100);
      const lines = doc.splitTextToSize(paymentNote, pageWidth - 80);
      doc.text(lines, 40, finalY + 16);
    }
  }

  // Footer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(140);
  doc.text(
    "Thank you for your business.",
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 30,
    { align: "center" }
  );

  const filename = `${bill.invoice_no}.pdf`;
  if (opts.returnBlob) {
    return doc.output("blob");
  }
  doc.save(filename);
}
